import { AssessmentStep } from "apps/user/learner/api/types";
import { isCommercialQuestionStep } from "./companySectionStep";

/**
 * Domains in first-seen order across the question steps, e.g.
 * ["Personal Stress", "Work Stress", "Movement", "Sleep", "Nutrition", ...].
 * Both question and section-intro screens resolve their progress number
 * through this single list, so a section that introduces several domains at
 * once (e.g. one Recovery section covering Movement/Sleep/Nutrition) still
 * agrees with the question screens that follow it — no separate per-screen
 * counters that can disagree.
 *
 * Excludes Commercial Use questions: they're never shown inline (collected
 * via the company-questions modal off the end screen), so their domains
 * would otherwise inflate the progress pill's total past what the learner
 * actually sees — mirrors mobile's steps filter (screens/Assessment/index.tsx).
 */
export const buildOrderedDomains = (steps: AssessmentStep[]): string[] => {
  const domains: string[] = [];
  const seen = new Set<string>();

  for (const step of steps) {
    if (step.__typename !== "AssessmentQuestionStep") continue;
    if (isCommercialQuestionStep(step)) continue;
    const domain = step.domain;
    if (!domain || seen.has(domain)) continue;
    seen.add(domain);
    domains.push(domain);
  }

  return domains;
};

/**
 * The 1-based progress number for a question step: the domain's position in
 * the ordered list.
 */
export const getQuestionProgressNumber = (
  step: AssessmentStep,
  orderedDomains: string[]
): number | null => {
  if (step.__typename !== "AssessmentQuestionStep" || !step.domain) {
    return null;
  }
  const index = orderedDomains.indexOf(step.domain);
  return index === -1 ? null : index + 1;
};

/**
 * The 1-based progress number for a section-intro (or info) step: resolved
 * by looking forward to the next question step's domain, not by counting
 * how many section steps have been seen — the two disagree the moment one
 * section introduces more than one domain.
 */
export const getSectionProgressNumber = (
  stepIndex: number,
  steps: AssessmentStep[],
  orderedDomains: string[]
): number | null => {
  for (let i = stepIndex + 1; i < steps.length; i++) {
    const next = steps[i];
    if (next.__typename === "AssessmentQuestionStep" && next.domain) {
      const index = orderedDomains.indexOf(next.domain);
      return index === -1 ? null : index + 1;
    }
  }
  return null;
};
